import { useEffect, useState } from "react";
import { faArrowRight, faInfoCircle, faFilter } from "@fortawesome/pro-regular-svg-icons";

import { parseNumber } from "common/utils/numbers";
import { useMatch } from "common/router/hooks";
import Icon from "common/components/Icon";
import Spinner from "common/components/Spinner";
import { IncentiveStatus } from "common/types/promotions";
import { useListSearchState } from "common/hooks";

import Page from "client/common/components/layouts/Page";
import Body from "client/common/components/layouts/Body";
import Container from "client/common/components/layouts/Container";
import Pagination from "client/common/components/layouts/Pagination";
import Results from "client/common/components/results/Results";
import FetchErrorPage from "client/common/components/errors/FetchErrorPage";
import LoadingPage from "client/common/components/loading/LoadingPage";
import LoadingContainer from "client/common/components/loading/LoadingContainer";
import PillButton from "client/common/components/buttons/PillButton";
import { useClinic } from "client/clinics/hooks";
import { Paths } from "client/routes";
import HomeBrowse, { CategoryCarousel } from "client/home/components/HomeBrowse";

import {
  getPromoCodePunctuation,
  getIncentiveProductFiltersFromSearch,
  getSearchString,
  toPromotionDateRange,
} from "../utils";
import {
  useBrowseQuery,
  useIncentiveItemsQuery,
  useIncentiveQuery,
  useIncentiveProductFilterOptionState,
} from "../hooks";
import { BrowseAllPatientKind } from "../types";
import PromotionItemCard from "./PromotionItemCard";
import PreFooter from "./PreFooter";
import ProductSearchFilterModal from "./ProductSearchFilterModal";
import ProductSearchFilterTags from "./ProductSearchFilterTags";
import { useIncentiveDrawer } from "../stackables";

import styles from "../styles/IncentivePage.module.less";

/**
 * This detail page is primarily meant for promotional emails etc sent out by clinics.
 * Nowhere in the client app currently links to it, but the urls will be accessed directly.
 */
const IncentivePage = () => {
  const initialSearch = {
    category_ids: [],
    manufacturer_ids: [],
    species: [],
    prescription_required: [],
    page: 1,
    page_size: 20,
  };

  const match = useMatch<Paths["products"]["incentive"]>();
  const parsedId = parseNumber(match.params.id, 0);
  const [search, updateSearch] = useListSearchState<Paths["products"]["incentive"]>(initialSearch);
  const [isFiltering, setIsFiltering] = useState(false);
  const { data: filterOptions } = useIncentiveProductFilterOptionState();
  const filters = getIncentiveProductFiltersFromSearch(search);
  const { open: openIncentiveDrawer } = useIncentiveDrawer();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [search.page]);

  const clinic = useClinic();
  const {
    data: incentive,
    error: incentiveError,
    isFetching: isIncentiveFetching,
    isLoading: isIncentiveLoading,
  } = useIncentiveQuery(parsedId);

  const {
    data: items,
    error: itemsError,
    isFetching: areItemsFetching,
    isLoading: areItemsLoading,
  } = useIncentiveItemsQuery(parsedId, {
    ...filters,
    page_size: 20,
    page: parseNumber(search.page, 1),
  });

  const {
    data: browse,
    isLoading: isBrowseLoading,
    error: browseError,
  } = useBrowseQuery({ species: BrowseAllPatientKind.ALL });

  const renderContent = () => {
    if (areItemsLoading) {
      return <Results.Loading description="Loading incentive items" />;
    }

    if (!items || itemsError) {
      return <Results.FetchError error={incentiveError} entityName="incentive item" />;
    }

    return (
      <div className={styles.content}>
        <div className={styles.info}>
          <div className={styles.label}>
            {isIncentiveFetching || areItemsFetching ? (
              <Spinner />
            ) : (
              getSearchString(items.paginator, "eligible item")
            )}
          </div>
          {filterOptions && (
            <ProductSearchFilterTags
              options={filterOptions.filters}
              value={filters}
              onChange={updateSearch}
            />
          )}
          <PillButton
            variant="secondary"
            size="s"
            type="button"
            onClick={() => setIsFiltering(true)}
          >
            <span>
              <Icon icon={faFilter} /> Filter
            </span>
          </PillButton>
        </div>
        {filterOptions && (
          <ProductSearchFilterModal
            options={filterOptions.filters}
            isVisible={isFiltering}
            onClose={() => setIsFiltering(false)}
            value={filters}
            onChange={updateSearch}
          />
        )}
        {items.results.length ? (
          <div className={styles.search}>
            <div className={styles.products}>
              {items.results.map((item) => (
                <PromotionItemCard
                  key={item.id}
                  classNames={{
                    product: styles.product,
                    content: styles.content,
                    image: styles.image,
                    facts: styles.facts,
                    names: styles.names,
                    name: styles.name,
                    description: styles.description,
                  }}
                  item={item}
                />
              ))}
            </div>
            <Pagination
              paginator={items.paginator}
              onPageChange={(page) => updateSearch({ page })}
              entityName="item"
            />
          </div>
        ) : (
          <Results.None header="No items found" />
        )}
      </div>
    );
  };

  const renderCarousel = () => {
    if (isBrowseLoading) {
      return <LoadingContainer />;
    }

    if (browseError || !browse) {
      return null;
    }

    return <CategoryCarousel categories={browse.categories} />;
  };

  if (isIncentiveLoading) {
    return <LoadingPage />;
  }

  if (!incentive || incentiveError) {
    return <FetchErrorPage error={incentiveError} entityName="incentive" />;
  }

  const styledPromoCodes = () => {
    const codes = incentive.promo_codes.map((promo) => promo.code);
    const codePunctuation = getPromoCodePunctuation(codes);
    return codes.map((code) => {
      return (
        <div className={styles.promoCodeContainer} key={code}>
          {codePunctuation[code]}
          <div className={styles.promoCode}>{code}</div>
        </div>
      );
    });
  };

  const getPromoCodePrefix = (promoMessage: string) => {
    const previousLastChar = promoMessage.slice(-1);
    const punctuationSet = new Set([".", "!", "?"]);
    return punctuationSet.has(previousLastChar) ? "" : ".";
  };

  const renderPromoCodeMessage = () => {
    const messagePrefix = getPromoCodePrefix(incentive.promo_message);
    return (
      <p className={styles.promoCodeMessage}>
        {messagePrefix} Use promo code {styledPromoCodes()} at checkout.
      </p>
    );
  };

  if (incentive.status !== IncentiveStatus.Live) {
    const errorMessage = `This ${incentive.promo_codes.length ? "promo code" : "discount"} ${
      incentive.status === IncentiveStatus.Expired ? "has expired" : "is not active"
    }`;

    return (
      <Page>
        <div className={styles.gradient} />
        <Container>
          <div className={styles.top}>
            <div className={styles.heading}>
              <div className={styles.title}>Uh oh! {errorMessage}...</div>
              <div className={styles.subtitle}>
                You can still browse products for your furry friend. Shop preventatives, diet, and
                medications below!
              </div>
            </div>
          </div>
        </Container>
        <Body className={styles.main}>
          <HomeBrowse clinic={clinic} />
        </Body>
      </Page>
    );
  }

  return (
    <Page>
      <div className={styles.gradient} />
      <Container>
        <div className={styles.top}>
          <div className={styles.heading}>
            <div className={styles.expiration}>
              {toPromotionDateRange(incentive.start_date, incentive.end_date)}
            </div>
            <div className={styles.titleRowContainer}>
              <div className={styles.title}>{incentive.promo_title}</div>
              <PillButton
                className={styles.button}
                variant="primary"
                onClick={() =>
                  openIncentiveDrawer({
                    id: incentive.id,
                    shouldDisplayItems: false,
                  })
                }
              >
                <div className={styles.buttonContent}>
                  <Icon icon={faInfoCircle} />
                  <div>View details</div>
                  <Icon className={styles.angleRight} icon={faArrowRight} />
                </div>
              </PillButton>
            </div>
            <div className={styles.subtitle}>
              {incentive.promo_message}
              {!!incentive.promo_codes.length && renderPromoCodeMessage()}
            </div>
          </div>
        </div>
      </Container>
      <div className={styles.main}>
        <Container>{renderContent()}</Container>
      </div>
      <div className={styles.browse}>
        <Container>
          <div className={styles.browseContent}>
            <h3 className={styles.browseTitle}>
              Browse our online store{" "}
              <span className={styles.accent}>& explore other top veterinarian picks</span>
            </h3>
            {renderCarousel()}
          </div>
        </Container>
      </div>
      <PreFooter image={clinic.storefront_prefooter_image} />
    </Page>
  );
};

export default IncentivePage;
